import {
  CalculatorOutlined,
  DisconnectOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { getOrderShortCode } from '@mahawi/eshop-common';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  type IAdminWithdrawalOrderCandidate,
  type IWarehouseOrderDetail,
  type IWarehouseOrderEventItem,
  type IWarehouseOrderItemDetail,
  type IWarehouseOrderItemPairedOrder,
  type IWarehouseOrderPairedOrder,
  type IWarehouseOrderPairingSuggestion,
  type IWarehouseOrderPairingSuggestionProduct,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  type GlobalToken,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import RelativeTime from '../components/relative-time';
import TableComponent from '../components/table';
import EventDescription from '../components/warehouse-order/event-description';
import {
  ACTOR_TYPE_LABEL,
  formatAmount,
  formatEur,
  WAREHOUSE_ORDER_EVENT_TYPE_LABEL,
  WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL,
} from '../components/warehouse-order/labels';
import ProfitText from '../components/warehouse-order/profit-text';
import {
  WarehouseOrderPairingStateTag,
  WarehouseOrderStatusTag,
} from '../components/warehouse-order/status-tag';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  warehouseOrderLoad,
  warehouseOrderOrderSearch,
  warehouseOrderPair,
  warehouseOrderRecalculate,
  warehouseOrderUnpair,
} from '../reducers/warehouse-order/reducer';
import { SPACING } from '../theme/tokens';

const { Text, Paragraph } = Typography;

type TItemRow = IWarehouseOrderItemDetail & { key: string };
type TPairedOrderRow = IWarehouseOrderPairedOrder & { key: string };
type TSuggestionRow = IWarehouseOrderPairingSuggestion & { key: string };
type TCandidateRow = IAdminWithdrawalOrderCandidate & { key: string };
type TEventRow = IWarehouseOrderEventItem & { key: string };
type TOnRow = Parameters<typeof TableComponent>[0]['onRow'];

const toOrderLink = (uuid: string): React.ReactElement => (
  <Link to={`/order/${uuid}`} label={getOrderShortCode(uuid)} openInNewTab />
);

const getItemRowBackground = (
  item: TItemRow,
  highlightedProductUuids: string[],
  token: GlobalToken,
): string => {
  if (item.product && highlightedProductUuids.includes(item.product.uuid)) {
    return token.colorWarningBg;
  }

  return item.pairedOrders.length > 0
    ? token.colorSuccessBg
    : token.colorErrorBg;
};

const getItemColumns = (currencyIsoCode: string): ColumnsType<TItemRow> => [
  { title: 'Code', dataIndex: 'productCode' },
  {
    title: 'Name',
    dataIndex: 'name',
    render: (name: string | null): string => name ?? EMPTY_VALUE,
  },
  { title: 'Quantity', dataIndex: 'quantity', align: 'right' },
  { title: 'Cancelled', dataIndex: 'cancelledQuantity', align: 'right' },
  { title: 'Delivered', dataIndex: 'deliveredQuantity', align: 'right' },
  {
    title: 'Unit price without VAT',
    align: 'right',
    render: (_value: string, record: TItemRow): string =>
      formatAmount(record.unitPrice, currencyIsoCode),
  },
  {
    title: 'VAT',
    dataIndex: 'taxRate',
    align: 'right',
    render: (taxRate: number): string => `${taxRate} %`,
  },
  {
    title: 'Our product',
    render: (_value: string, record: TItemRow): React.ReactNode => {
      if (record.product) {
        return (
          <Link
            to={`/product/${record.product.uuid}`}
            label="Product"
            openInNewTab
          />
        );
      }

      if (record.warehouseProduct) {
        return (
          <Link
            to={`/downloader/warehouse-product/${record.warehouseProduct.uuid}`}
            label="Warehouse product, not paired"
            openInNewTab
          />
        );
      }

      return <Text type="secondary">Unknown product</Text>;
    },
  },
  {
    title: 'Paired orders',
    render: (_value: string, record: TItemRow): React.ReactNode => {
      if (record.pairedOrders.length === 0) {
        return <Text type="secondary">Not paired</Text>;
      }

      return (
        <Space orientation="vertical" size={SPACING.none}>
          {record.pairedOrders.map(
            (
              pairedOrder: IWarehouseOrderItemPairedOrder,
            ): React.ReactElement => (
              <Space key={pairedOrder.uuid} size={SPACING.xs}>
                <Text>{pairedOrder.quantity}×</Text>
                {toOrderLink(pairedOrder.uuid)}
              </Space>
            ),
          )}
        </Space>
      );
    },
  },
];

const eventColumns: ColumnsType<TEventRow> = [
  {
    title: 'When',
    dataIndex: 'createdAt',
    render: (createdAt: string): React.ReactElement => (
      <RelativeTime value={createdAt} />
    ),
  },
  {
    title: 'Event',
    dataIndex: 'type',
    render: (_value: string, record: TEventRow): string =>
      WAREHOUSE_ORDER_EVENT_TYPE_LABEL[record.type],
  },
  {
    title: 'Actor',
    render: (_value: string, record: TEventRow): string =>
      record.actorEmail ?? ACTOR_TYPE_LABEL[record.actorType],
  },
  {
    title: 'Detail',
    render: (_value: string, record: TEventRow): React.ReactElement => (
      <EventDescription event={record} />
    ),
  },
];

function WarehouseOrderContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const { uuid = '' } = useParams<{ uuid: string }>();
  const detail = useSelector((s: RootState) => s.WarehouseOrder.detail);
  const inProcess = useSelector((s: RootState) => s.WarehouseOrder.inProcess);
  const searchResults = useSelector(
    (s: RootState) => s.WarehouseOrder.orderSearchResults,
  );
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const { token } = theme.useToken();
  const [highlightedProductUuids, setHighlightedProductUuids] = useState<
    string[]
  >([]);

  useEffect((): void => {
    dispatch(warehouseOrderLoad(uuid));
  }, [dispatch, uuid]);

  const pairButton = (orderUuid: string): React.ReactElement => (
    <Button
      size="small"
      type="primary"
      icon={<LinkOutlined />}
      disabled={inProcess}
      onClick={(): void => {
        dispatch(warehouseOrderPair(uuid, orderUuid));
      }}
    >
      Pair
    </Button>
  );

  const pairedOrderColumns = useMemo<ColumnsType<TPairedOrderRow>>(
    () => [
      {
        title: 'Order',
        render: (_value: string, record: TPairedOrderRow): React.ReactElement =>
          toOrderLink(record.uuid),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        render: (createdAt: string): string =>
          dayjs(createdAt).format(EDateFormat.DATE),
      },
      { title: 'Status', dataIndex: 'orderStatus' },
      {
        title: 'Paired by',
        render: (_value: string, record: TPairedOrderRow): string =>
          record.pairedBy ??
          WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL[record.source],
      },
      {
        title: 'Our price without VAT',
        align: 'right',
        render: (_value: string, record: TPairedOrderRow): string =>
          formatAmount(record.priceWithoutTax, record.currencyIsoCode),
      },
      {
        title: 'Supplied pieces',
        align: 'right',
        render: (
          _value: string,
          record: TPairedOrderRow,
        ): React.ReactElement => (
          <Text
            type={
              record.coveredQuantity < record.orderedQuantity
                ? 'warning'
                : undefined
            }
          >
            {record.coveredQuantity} / {record.orderedQuantity}
          </Text>
        ),
      },
      {
        title: 'Cost',
        align: 'right',
        render: (_value: string, record: TPairedOrderRow): string =>
          formatEur(record.purchasePriceEur),
      },
      {
        title: 'Revenue',
        align: 'right',
        render: (_value: string, record: TPairedOrderRow): string =>
          formatEur(record.revenueEur),
      },
      {
        title: 'Profit',
        align: 'right',
        render: (
          _value: string,
          record: TPairedOrderRow,
        ): React.ReactElement =>
          record.coveredQuantity < record.orderedQuantity ? (
            <Tooltip title="Not every piece of this order comes from this warehouse order, see the order detail for the total profit.">
              <Tag color="orange">Partial</Tag>
            </Tooltip>
          ) : (
            <ProfitText value={record.profitEur} />
          ),
      },
      {
        title: '',
        render: (
          _value: string,
          record: TPairedOrderRow,
        ): React.ReactElement => (
          <Popconfirm
            title="Unpair this order?"
            description="Automatic pairing stops for this warehouse order."
            onConfirm={(): void => {
              dispatch(warehouseOrderUnpair(uuid, record.uuid));
            }}
          >
            <Button
              size="small"
              danger
              icon={<DisconnectOutlined />}
              disabled={inProcess}
            >
              Unpair
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [dispatch, inProcess, uuid],
  );

  const suggestionColumns = useMemo<ColumnsType<TSuggestionRow>>(
    () => [
      {
        title: 'Order',
        render: (_value: string, record: TSuggestionRow): React.ReactElement =>
          toOrderLink(record.uuid),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        render: (createdAt: string): string =>
          dayjs(createdAt).format(EDateFormat.DATE),
      },
      { title: 'Status', dataIndex: 'orderStatus' },
      {
        title: 'Our price without VAT',
        align: 'right',
        render: (_value: string, record: TSuggestionRow): string =>
          formatAmount(record.priceWithoutTax, record.currencyIsoCode),
      },
      {
        title: 'Matching pieces',
        align: 'right',
        render: (_value: string, record: TSuggestionRow): string =>
          `${record.matchedQuantity} / ${record.orderedQuantity}`,
      },
      {
        title: 'Products',
        render: (
          _value: string,
          record: TSuggestionRow,
        ): React.ReactElement => (
          <Space orientation="vertical" size={SPACING.none}>
            {record.products.map(
              (
                product: IWarehouseOrderPairingSuggestionProduct,
              ): React.ReactElement => {
                const name: string =
                  getTranslation(product.names, languageType) ?? EMPTY_VALUE;

                return (
                  <Space key={product.uuid} size={SPACING.xs}>
                    <Text
                      type={
                        product.isInWarehouseOrder ? undefined : 'secondary'
                      }
                    >
                      {product.count}×
                    </Text>
                    <Link
                      to={`/product/${product.uuid}`}
                      label={
                        product.isInWarehouseOrder ? (
                          name
                        ) : (
                          <Tooltip title="Not in this warehouse order">
                            <Text type="secondary">{name}</Text>
                          </Tooltip>
                        )
                      }
                      openInNewTab
                    />
                  </Space>
                );
              },
            )}
          </Space>
        ),
      },
      {
        title: '',
        render: (_value: string, record: TSuggestionRow): React.ReactElement =>
          pairButton(record.uuid),
      },
    ],
    [dispatch, inProcess, uuid, languageType],
  );

  const candidateColumns = useMemo<ColumnsType<TCandidateRow>>(
    () => [
      {
        title: 'Order',
        render: (_value: string, record: TCandidateRow): React.ReactElement =>
          toOrderLink(record.uuid),
      },
      { title: 'Billing name', dataIndex: 'billingName' },
      { title: 'Billing email', dataIndex: 'billingEmail' },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        render: (createdAt: string): string =>
          dayjs(createdAt).format(EDateFormat.DATE),
      },
      { title: 'Status', dataIndex: 'orderStatusCode' },
      {
        title: 'Total',
        align: 'right',
        render: (_value: string, record: TCandidateRow): string =>
          formatAmount(record.total, record.currencyIsoCode),
      },
      {
        title: '',
        render: (_value: string, record: TCandidateRow): React.ReactElement =>
          pairButton(record.uuid),
      },
    ],
    [dispatch, inProcess, uuid],
  );

  if (detail === undefined) {
    return <Skeleton active />;
  }

  if (detail === null) {
    return <Alert type="warning" showIcon title="Warehouse order not found." />;
  }

  const order: IWarehouseOrderDetail = detail;

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title={`${order.warehouse.name} ${order.code}`}
        onReload={(): void => {
          dispatch(warehouseOrderLoad(uuid));
        }}
        isReloadDisabled={inProcess}
        actions={
          <Tooltip title="Recalculate purchase cost, supplier fees and profit">
            <Button
              icon={<CalculatorOutlined />}
              disabled={inProcess}
              onClick={(): void => {
                dispatch(warehouseOrderRecalculate(uuid));
              }}
            >
              Recalculate
            </Button>
          </Tooltip>
        }
        uuid={order.uuid}
      />

      <Descriptions
        bordered
        column={{ xs: 1, sm: 2, md: 3 }}
        items={[
          {
            key: 'status',
            label: 'Status',
            children: <WarehouseOrderStatusTag status={order.status} />,
          },
          {
            key: 'pairing',
            label: 'Pairing',
            children: (
              <WarehouseOrderPairingStateTag
                pairingState={order.pairingState}
              />
            ),
          },
          {
            key: 'orderedAt',
            label: 'Ordered',
            children: dayjs(order.orderedAt).format(EDateFormat.DATE_TIME),
          },
          {
            key: 'total',
            label: 'Total',
            children: (
              <Space orientation="vertical" size={SPACING.none}>
                <Text>
                  {formatAmount(order.totalWithoutTax, order.currencyIsoCode)}{' '}
                  without VAT
                </Text>
                <Text type="secondary">
                  {formatAmount(order.totalWithTax, order.currencyIsoCode)} with
                  VAT
                </Text>
              </Space>
            ),
          },
          {
            key: 'fees',
            label: 'Supplier fees',
            children: (
              <Space orientation="vertical" size={SPACING.none}>
                <Text>
                  {formatAmount(order.feesWithoutTax, order.currencyIsoCode)}
                </Text>
                <Text type="secondary">{formatEur(order.feesEur)}</Text>
              </Space>
            ),
          },
          {
            key: 'unallocated',
            label: 'Cost not assigned to our orders',
            children: formatAmount(
              order.unallocatedCost,
              order.currencyIsoCode,
            ),
          },
          {
            key: 'invoices',
            label: 'Invoices',
            children:
              order.invoiceCodes.length > 0
                ? order.invoiceCodes.join(', ')
                : EMPTY_VALUE,
          },
          {
            key: 'externalId',
            label: 'Warehouse order ID',
            children: order.externalId,
          },
          {
            key: 'syncedAt',
            label: 'Synced',
            children: <RelativeTime value={order.syncedAt} />,
          },
          {
            key: 'note',
            label: 'Note',
            span: 'filled',
            children: order.note ? (
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {order.note}
              </Paragraph>
            ) : (
              EMPTY_VALUE
            ),
          },
        ]}
      />

      <Divider titlePlacement="left">Our orders</Divider>
      <TableComponent
        columns={
          pairedOrderColumns as Parameters<typeof TableComponent>[0]['columns']
        }
        dataSource={order.pairedOrders.map(
          (pairedOrder: IWarehouseOrderPairedOrder): TPairedOrderRow => ({
            key: pairedOrder.uuid,
            ...pairedOrder,
          }),
        )}
        isLoading={inProcess}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        emptyText="Not paired with any of our orders"
      />

      <Divider titlePlacement="left">Pair with an order</Divider>
      <Space orientation="vertical" size={SPACING.md} style={{ width: '100%' }}>
        <Text strong>Suggested orders with the same products</Text>
        <TableComponent
          columns={
            suggestionColumns as Parameters<typeof TableComponent>[0]['columns']
          }
          dataSource={order.suggestions.map(
            (suggestion: IWarehouseOrderPairingSuggestion): TSuggestionRow => ({
              key: suggestion.uuid,
              ...suggestion,
            }),
          )}
          isLoading={false}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          emptyText="No unpaired order with these products around the order date"
          onRow={
            ((
              record: TSuggestionRow,
            ): React.HTMLAttributes<HTMLTableRowElement> => ({
              onMouseEnter: (): void => {
                setHighlightedProductUuids(
                  record.products.map(
                    (
                      product: IWarehouseOrderPairingSuggestionProduct,
                    ): string => product.uuid,
                  ),
                );
              },
              onMouseLeave: (): void => {
                setHighlightedProductUuids([]);
              },
            })) as TOnRow
          }
        />
        <Input.Search
          placeholder="Search orders by short code, UUID or billing email"
          enterButton="Search"
          allowClear
          disabled={inProcess}
          onSearch={(query: string): void => {
            if (query.trim()) {
              dispatch(warehouseOrderOrderSearch(query.trim()));
            }
          }}
        />
        {searchResults && (
          <TableComponent
            columns={
              candidateColumns as Parameters<
                typeof TableComponent
              >[0]['columns']
            }
            dataSource={searchResults.map(
              (candidate: IAdminWithdrawalOrderCandidate): TCandidateRow => ({
                key: candidate.uuid,
                ...candidate,
              }),
            )}
            isLoading={false}
            pagination={false}
            size="small"
            emptyText="No orders found"
          />
        )}
      </Space>

      <Divider titlePlacement="left">Items</Divider>
      <TableComponent
        columns={
          getItemColumns(order.currencyIsoCode) as Parameters<
            typeof TableComponent
          >[0]['columns']
        }
        dataSource={order.items.map(
          (item: IWarehouseOrderItemDetail): TItemRow => ({
            key: item.externalId,
            ...item,
          }),
        )}
        isLoading={false}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        onRow={
          ((record: TItemRow): React.HTMLAttributes<HTMLTableRowElement> => ({
            style: {
              backgroundColor: getItemRowBackground(
                record,
                highlightedProductUuids,
                token,
              ),
              transition: `background-color ${token.motionDurationMid}`,
            },
          })) as TOnRow
        }
      />

      <Divider titlePlacement="left">History</Divider>
      <TableComponent
        columns={
          eventColumns as Parameters<typeof TableComponent>[0]['columns']
        }
        dataSource={order.events.map(
          (event: IWarehouseOrderEventItem): TEventRow => ({
            key: event.uuid,
            ...event,
          }),
        )}
        isLoading={false}
        pagination={false}
        size="small"
      />
    </Space>
  );
}

export default WarehouseOrderContainer;
